<script>
import { shippingMethodProps, useShippingMethod } from "./mixin";
import Button from "@/components/atoms/button";
import Icon from "@/components/atoms/icon";
import FormRadio from "@/components/molecules/form-radio";
import Card from "@/components/molecules/card";
import Alert from "@/components/molecules/alert";
import ButtonIcon from "@/components/molecules/button-icon";
import PopinAnchor from "@/components/molecules/popin-anchor";
import Divider from "@/components/atoms/divider";

import yper2 from "@/assets/images/livraison/yper-2.png";
import delipop from "@/assets/images/livraison/delipop.png";

const LazyImage = defineAsyncComponent(() =>
  import("@/components/atoms/lazy-image")
);
const Popin = defineAsyncComponent(() =>
  import("@/components/molecules/popin")
);
const CmsFetcher = defineAsyncComponent(() =>
  import("@/containers/cms-fetcher")
);

export default {
  name: "ShippingMethod",
  components: {
    Card,
    Icon,
    FormRadio,
    Button,
    Alert,
    ButtonIcon,
    PopinAnchor,
    Divider,
    LazyImage,
    Popin,
    CmsFetcher
  },
  props: {
    ...shippingMethodProps()
  },
  emits: [
    "click",
    "showOrderDetail",
    "openPopinTarifsYper",
    "showProduitsIncompatibles"
  ],
  setup(props) {
    const { iconName, description, hasFraisLiquide, isLC, isDelipop } =
      useShippingMethod(props);

    return {
      iconName,
      description,
      hasFraisLiquide,
      isLC,
      isDelipop,
      yper2,
      delipop
    };
  }
};
</script>

<template>
  <Card
    class="c-shipping-method"
    :class="[
      disabled && 'c-shipping-method--disabled',
      selected && 'c-shipping-method--selected',
      ((infos && (infos.frais_inclus || infos.libelle_frais_inclus)) ||
        (!disabled && isLadActif) ||
        isLC ||
        (disabled && raisonsImpossible.length > 0)) &&
        'c-shipping-method--infos',
      isLadActif && 'c-shipping-method--abolad'
    ]"
    :elevation="disabled ? '' : 'md'"
    radius="md"
  >
    <template #header>
      <Button
        class="c-shipping-method__selector u-flex u-flex-column"
        :disabled="disabled"
        :aria-expanded="selected ? 'true' : 'false'"
        :aria-current="selected ? 'true' : 'false'"
        @click="$emit('click')"
      >
        <div class="u-flex u-flex-align-center u-full-width u-flex-wrap@lg">
          <FormRadio
            class="c-shipping-method__radio"
            :checked="selected"
            :disabled="disabled"
            aria-hidden="true"
            tabindex="-1"
          />
          <!-- TODO : composant image -->
          <Icon
            class="c-shipping-method__icon"
            :name="iconName"
          />
          <div class="c-shipping-method__label">
            <div
              class="u-flex u-flex-align-center"
              :class="isLadActif ? 'u-mb-xxs@lg' : false"
            >
              <p>
                {{ modeCommande.label_long }}
              </p>
              <PopinAnchor
                link
                open-on-hover
                infobulle
                popin-size="md"
                class="u-ml-sm"
                tag="div"
                aria-label="Plus information sur ce mode de livraison"
              >
                <template #button-content>
                  <ButtonIcon
                    icon-name="info"
                    size="xs"
                    radius="full"
                    variant="primary"
                    icon-color="primary"
                    icon-color-hover="white"
                    outline
                    tag="div"
                    :disabled="disabled"
                  >
                  </ButtonIcon>
                </template>
                <p
                  class="u-font-14 u-color-text"
                  v-html="description"
                ></p>
              </PopinAnchor>
            </div>
          </div>
          <div
            v-if="!disabled && isLadActif"
            class="c-shipping-method__label-description u-none u-block@lg u-mb-xxs@lg"
          >
            Profitez de votre abonnement à la livraison
          </div>
          <div
            v-else-if="modeCommande.description_courte"
            class="c-shipping-method__label-description u-none u-block@lg u-mb-xxs@lg"
          >
            {{ modeCommande.description_courte }}
          </div>
          <div
            v-if="typePanier === 'ldd' && lddDelaiLinkLabel"
            class="c-shipping-method__time"
          >
            <Button
              link
              @click="$emit('showOrderDetail')"
            >
              {{ lddDelaiLinkLabel }}
            </Button>
          </div>
          <template v-else-if="infos">
            <p class="c-shipping-method__time">
              {{ infos.planning_mini_txt }}
            </p>
            <div class="c-shipping-method__cost">
              <p
                v-if="isLadActif"
                class="u-font-500"
                :class="disabled ? '' : 'u-color-green-2'"
              >
                {{ infos.tarif_mini_txt }}<br /><span
                  class="u-font-13 u-font-400"
                  :class="disabled ? '' : 'u-color-text-light'"
                >
                  grâce à votre abonnement à la livraison
                </span>
              </p>
              <p v-else>
                {{ infos.tarif_mini_txt }}
              </p>
            </div>
          </template>
        </div>
      </Button>
      <div
        v-if="isLC"
        class="c-shipping-method__tarif"
      >
        <Button
          link
          variant="text-light"
          @click="$emit('openPopinTarifsYper')"
        >
          Voir les tarifs
        </Button>
      </div>
      <div
        v-if="infos && (infos.frais_inclus || infos.libelle_frais_inclus)"
        class="c-shipping-method__infos"
      >
        <template v-if="infos.frais_inclus">
          <p
            v-for="(frais, index) in infos.frais_inclus"
            :key="`frais-${index}`"
            class="u-color-primary u-font-14 u-text-right u-mr-xs"
          >
            dont {{ lowercaseFirstLetter(frais.libelle) }} de
            {{ formatPrix(frais.montant) }} €
          </p>
        </template>
        <Alert
          v-if="infos.libelle_frais_inclus"
          radius="md"
          class="c-shipping-method__alert c-alert--pointer c-alert--pointer-top-right"
        >
          <p class="u-font-15 u-color-grey-1">
            {{ infos.libelle_frais_inclus }}
          </p>
          <Button
            v-if="hasFraisLiquide"
            link
            :to="`/panier?type_panier=${typePanier}`"
            class="u-mt-xs"
          >
            Modifier les quantités de liquides de ma commande
          </Button>
          <!-- <PopinAnchor link>
              <template
                #button-content
                variant="primary"
              >
                <span class="u-font-15">Modifier les quantités de liquides de ma commande</span>
              </template>
              <p class="u-text-lg u-color-primary"><strong>Supplément liquide de 8,00&nbsp;€</strong></p>
              <p class="u-font-14">Le volume total des boissons de votre commande est de 189 litres.
                En Retrait en casier, vous pouvez faire livrer jusque 12 litres maximum.</p>
            </PopinAnchor> -->
        </Alert>
      </div>
      <div
        v-if="!disabled && isLadActif"
        class="u-pb-lg u-pl-md u-pr-lg c-shipping-method__label-description u-font-500 u-none@lg"
      >
        Profitez de votre abonnement à la livraison
      </div>
      <div
        v-else-if="modeCommande.description_courte && !isLC"
        class="u-pb-lg u-pl-md u-pr-lg c-shipping-method__label-description u-none@lg"
      >
        {{ modeCommande.description_courte }}
      </div>

      <template v-if="disabled">
        <div
          v-if="raisonsImpossible"
          class="c-shipping-method__infos"
        >
          <div
            v-for="(raison, index) in raisonsImpossible"
            :key="`raison-${index}-${raison.type_raison}`"
          >
            <p class="u-mt-sm">
              {{ raison.raison }}
            </p>
            <Button
              v-if="raison.type_raison === 'produit_non_compatible'"
              link
              class="u-mt-xs"
              @click="$emit('showProduitsIncompatibles')"
            >
              Voir les produits incompatibles
            </Button>
            <Button
              v-if="raison.type_raison === 'frais_bloquant'"
              link
              :to="`/panier?type_panier=${typePanier}`"
              class="u-mt-xs"
            >
              Voir les boissons de ma commande
            </Button>
            <Divider
              v-if="index < raisonsImpossible.length - 1"
              dotted
              class="u-my-sm"
            />
          </div>
        </div>
        <div
          v-if="isLadActif && montantManquantLad > 0"
          class="c-shipping-method__infos"
        >
          Il manque {{ formatPrix(montantManquantLad) }} € à votre commande pour
          profiter de votre abonnement à la livraison
        </div>
      </template>
      <template v-if="infos && (isLC || isDelipop)">
        <div
          :class="disabled && 'c-shipping-method__collab--disabled'"
          class="c-shipping-method__collab"
        >
          <Card
            :class="isLC ? 'c-shipping-method__collab-encart u-mt-sm' : 'c-shipping-method__delipop-encart'"
            border-color="border"
            radius="md"
          >
            <div class="u-flex u-flex-align-center">
              <div class="u-flex u-flex-align-center u-flex-shrink-none u-mx-sm">
                <LazyImage
                  :src="isLC ? yper2 : delipop"
                  width="88"
                  height="33"
                  alt=""
                />
              </div>

              <div>
                <p v-if="isLC">
                  Choisissez de confier la livraison de votre commande à un livreur particulier sélectionné par la plateforme collaborative Yper.
                  <Button
                    :variant="disabled ? 'grey-2' : 'primary'"
                    link
                    @click="$refs.popinInfo.show()"
                  >
                    En savoir plus
                  </Button>
                </p>
                <p v-if="isDelipop">
                  Retirez vos courses 7/7j en autonomie, dans votre drive piéton Delipop.
                  <Button
                    :variant="disabled ? 'grey-2' : 'primary'"
                    link
                    target="__blank"
                    href="/infos-et-services/les-magasins-cora/profitez-des-services/delipop"
                  >
                    En savoir plus
                  </Button>
                </p>
              </div>
            </div>
          </Card>
        </div>
      </template>
    </template>

    <template v-if="selected">
      <slot />
    </template>
    <Popin
      v-if="isLC"
      ref="popinInfo"
      aria-label="Informations livraison collaborative"
      class="c-popin--info-shipping"
      size="lg"
    >
      <CmsFetcher :link="isLC ? '/infos-et-services/sinformer/livraison-collaborative' : '/infos-et-services/les-magasins-cora/profitez-des-services/delipop'" />
    </Popin>
  </Card>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/shipping-method.scss";
</style>
